import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wizard } from 'react-use-wizard';

import { Modal } from '@/components/common/modal';

import { useSettings } from '../../hooks/use-settings';
import { CameraType } from '../../types';
import {
  CalibrationCloseStep,
  CalibrationStep,
  CountingChamberStep,
  InstructionStep,
  SelectMicroscopeStep,
} from './steps';
import { StepWrapper } from './steps/step-wrapper';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  /** callback when user clicks on back button */
  onBack: () => void;
}

export const CameraCalibrationWizard: React.FC<IProps> = ({ toggle, isOpen, onBack }) => {
  const { t } = useTranslation();
  const { settings, setSettings } = useSettings();

  const [showClose, setShowClose] = useState<boolean>(false);
  const [showExitDialog, setShowExitDialog] = useState<boolean>(false);

  const handleSwitchBackToMobileCam = () => {
    setSettings({ ...settings, camera: CameraType.Oculyze });
    toggle();
    onBack();
  };

  // idealy we should replace this logic with a higher level provider/context
  // but wanted to keep it simple for now, please change it if logic become more complex
  const handleCloseWithoutSaving = () => {
    setShowExitDialog(true);
  };

  const handleStepChange = (step: number) => {
    // show close button for step 0 (intro), 1 (counting chamber) and 2 (calibration) only
    if ([0, 1, 2].includes(step)) {
      setShowClose(true);
    } else {
      setShowClose(false);
    }

    // reset exit state once we come back from the close step
    setShowExitDialog(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      hideCloseBtn={!showClose}
      conatinerClassnames="xl:w-2/3 overflow-y-auto max-h-screen"
      toggle={handleCloseWithoutSaving}
      title={t('calibration.title')}
    >
      <Wizard
        wrapper={
          <StepWrapper onStepChange={handleStepChange} showExitDialog={showExitDialog} />
        }
      >
        <SelectMicroscopeStep onSwitchBackToMobileCam={handleSwitchBackToMobileCam} />
        <InstructionStep />
        <CountingChamberStep />
        <CalibrationStep onSave={toggle} />
        <CalibrationCloseStep onSwitchBackToMobileCam={handleSwitchBackToMobileCam} />
      </Wizard>
    </Modal>
  );
};
